<template>
    <div class="message-send-modal">

        <b-btn size="sm" variant="warning"
               @click="showModal()">
            {{ $i18n.gt('LABEL_SEND_MESSAGE') }}
            <shs-icon name="envelope-closed"></shs-icon>
        </b-btn>

        <!-- modal -->
        <b-modal size="xl"
                 body-class="message-send-modal__modal"
                 :id="modalId">
            <template v-slot:modal-header>
                <h5>{{ $i18n.gt('LABEL_SEND_MESSAGE') }}</h5>
                <div class="float-right">
                    <patient-id :participant="participant"></patient-id>
                </div>
            </template>

            <b-tabs>
                <b-tab title="Select Template">
                    <b-card-body>
                        <template v-if="messageOptions.length">
                            <b-form-radio-group stacked
                                                :options="messageOptions"
                                                v-model="selectedMessageId">
                            </b-form-radio-group>
                        </template>
                        <template v-else>
                            <b-alert variant="info" show>
                                {{ $i18n.gt('MESSAGE_NO_MESSAGES_FOUND') }}
                            </b-alert>
                        </template>
                    </b-card-body>
                </b-tab>
                <b-tab>
                    <template #title>
                        Select Attachments
                        <span v-if="selectedAttachmentIds.length">
                            ({{ selectedAttachmentIds.length }})
                        </span>
                    </template>
                    <b-card-body>
                        <template v-if="attachmentOptionGroups.length">
                            <div v-for="group in attachmentOptionGroups" :key="group.heading">
                                <h3>{{ group.heading }}</h3>
                                <b-form-checkbox-group stacked
                                                       :options="group.options"
                                                       v-model="selectedAttachmentIds">
                                </b-form-checkbox-group>
                            </div>
                        </template>
                        <template v-else>
                            <b-alert variant="info" show>
                                {{ $i18n.gt('MESSAGE_NO_DOCUMENTS_FOUND') }}
                            </b-alert>
                        </template>
                    </b-card-body>
                </b-tab>
                <b-tab title="Compose">
                    <b-card-body>
                        <b-row>
                            <b-col cols="12" md="12">
                                <b-alert variant="info" show
                                         class="message-send-modal__subject-tip"
                                         v-html="$i18n.gt('MESSAGE_SUBJECT_TIP')">
                                </b-alert>
                            </b-col>
                        </b-row>
                        <b-row class="mb-2">
                            <b-col cols="12" md="2">
                                <label>{{ $i18n.gt('LABEL_MESSAGE_SUBJECT') }}</label>
                            </b-col>
                            <b-col cols="12" md="7">
                                <b-form-input v-model="subject">
                                </b-form-input>
                            </b-col>
                            <b-col cols="12" lg="3" class="text-right">
                                <b-form-checkbox v-model="bccPractice">
                                    Bcc To My Practice
                                </b-form-checkbox>
                            </b-col>
                        </b-row>
                        <b-row class="mb-2">
                            <b-col cols="12" md="2">
                                <label>{{ $i18n.gt('LABEL_MESSAGE_BODY') }}</label>
                            </b-col>
                            <b-col cols="12" md="7">
                                <b-form-textarea class="mb-3" v-model="body">
                                </b-form-textarea>
                            </b-col>
                            <b-col cols="12" md="3" class="text-right">
                                <b-btn variant="warning"
                                       :disabled="!canSendMessage"
                                       @click="sendMessage()">
                                    {{ $i18n.gt('LABEL_SEND') }}
                                    <shs-icon name="envelope-closed"></shs-icon>
                                </b-btn>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-tab>
            </b-tabs>
            <template #modal-footer>
                <b-btn class="btn-ghost-blue float-right"
                       @click="hideModal()">
                    Cancel
                </b-btn>
            </template>
        </b-modal>

    </div>
</template>

<script>

import * as _ from 'lodash'
import PractitionerPatientMixin from '@/providers/PractitionerPatientMixin'
import Attachments from '@/providers/Attachments'
import Messages from '@/providers/Messages'

/**
 * Button and modal for sending a Message to a Participant
 */
export default {
    name: 'MessageSendModal',
    mixins: [PractitionerPatientMixin],
    props: {
        participantId: Number
    },
    data() {
        return {
            practice: null,
            messageOptions: [],
            selectedMessageId: null,
            subject: '',
            body: '',
            bccPractice: false,
            attachmentOptionGroups: [],
            attachmentOptions: [],
            selectedAttachmentIds: []
        }
    },
    computed: {
        modalId() {
            return 'messageSendModal'
        },
        participantFullname() {
            return this.participant.fullname
        },
        canSendMessage() {
            return !!this.subject && !!this.body
        }
    },
    methods: {
        showModal() {
            this.selectedMessageId = null
            this.selectedAttachmentIds = []
            this.$bvModal.show(this.modalId)
        },
        hideModal() {
            this.$bvModal.hide(this.modalId)
        },
        async sendMessage() {
            try {
                await this.$api.sendMessage(this,
                    this.subject,
                    this.body,
                    this.bccPractice,
                    this.participant.id,
                    this.practice.id,
                    this.selectedAttachmentIds,
                    this.$i18n.gt('PATIENT_MESSAGE_FOOTER'))
                this.$toasted.show(this.$i18n.gt('MESSAGE_MESSAGE_SENT'), { duration: 3000 })
                this.hideModal()
                this.refresh()
            } catch (e) {
                this.$utils.handleError(e, 'Could not send message')
            }
        },
        refresh() {
            // update Last Message Sent (i.e. last_invitation) display
            this.$store.dispatch('getPatientParticipant',
                {
                    participantId: this.participantId,
                    force: true
                })
        }
    },
    watch: {
        // on radio selection, populate subject/body
        selectedMessageId(newVal, oldVal) {
            const message = _.find(this.practice.messages, (message) => {
                return message.id == newVal
            })
            if (message) {
                this.subject = message.subject
                this.body = Messages.buildSalutation(this.participant.fullname) + '\n\n' + message.body
            } else {
                this.subject = ''
                this.body = ''
            }
        }
    },

    async mounted() {
        const practice = await this.$api.getItem(this, 'practice', this.provider.practice_id)
        this.practice = practice

        this.messageOptions = _.map(practice.messages, (message) => {
            return { value: message.id, text: message.name }
        })
        this.attachmentOptions = _.map(practice.attachments, (att) => {
            return { value: att.id, text: att.name }
        })

        const attachmentGroups = Attachments.getGroupedSortedAttachments(practice.attachments)
        this.attachmentOptionGroups = _.map(_.keys(attachmentGroups), (group) => {
            return {
                heading: group,
                options: _.map(attachmentGroups[group], (att) => {
                    return { value: att.id, text: att.name }
                })
            }
        })
    }
}
</script>

<style lang="scss">

.message-send-modal {
    display: inline-block;
}

.message-send-modal__modal {
    min-height: 400px;

    textarea {
        min-height: 200px;
    }
}

.message-send-modal__subject-tip {
    max-width: unset !important;

    strong {
        font-weight: 1000;
    }
}

</style>
